/**
 * Congratulations message utility
 * Provides dynamic, contextual congratulations messages for completed chess lines
 */

import congratulationsData from '../data/congratulations.json';

/**
 * Get a random congratulations message based on context
 * @param {Object} context - Context about the completion
 * @param {string} context.openingName - Name of the opening
 * @param {string} context.lineName - Name of the line/variation
 * @param {number} context.moveCount - Total moves in the line
 * @param {number} context.mistakes - Number of mistakes made
 * @param {number} context.completionTime - Time taken in seconds
 * @param {boolean} context.firstAttempt - Whether this was the first attempt
 * @param {number} context.streak - Current completion streak
 * @returns {Object} Congratulations message object
 */
export function getCongratulationsMessage(context = {}) {
  const {
    openingName = '',
    lineName = '',
    moveCount = 0,
    mistakes = 0,
    completionTime = 0,
    firstAttempt = false,
    streak = 0
  } = context;

  // Determine message category based on context
  let preferredCategories = ['general'];
  
  // Perfect play (no mistakes)
  if (mistakes === 0) {
    preferredCategories.push('achievement', 'tactical', 'strategic');
  }
  
  // Fast completion (< 60 seconds)
  if (completionTime > 0 && completionTime < 60) {
    preferredCategories.push('speed');
  }
  
  // First attempt success
  if (firstAttempt) {
    preferredCategories.push('first_attempt', 'achievement');
  }
  
  // Long sequences (many moves)
  if (moveCount > 15) {
    preferredCategories.push('persistence_pays', 'analytical');
  }
  
  // High streak
  if (streak > 3) {
    preferredCategories.push('grandmaster_level', 'elite');
  }

  // Filter messages by preferred categories
  const eligibleMessages = congratulationsData.congratulations.filter(msg => 
    preferredCategories.includes(msg.category)
  );

  // Weighted random selection
  const selectedMessage = weightedRandomSelect(eligibleMessages);
  
  if (!selectedMessage) {
    // Fallback to default message
    return {
      ...congratulationsData.fallbackMessages,
      message: getRandomFromArray(congratulationsData.fallbackMessages.messages),
      bonuses: [],
      tip: getRandomFromArray(congratulationsData.tips)
    };
  }

  // Get bonuses based on performance
  const bonuses = getBonuses({
    mistakes,
    completionTime,
    firstAttempt,
    streak
  });

  // Select random message from the chosen category
  const message = getRandomFromArray(selectedMessage.messages);
  
  // Add contextual information
  const contextualMessage = message.replace('{opening}', openingName)
                                  .replace('{line}', lineName);

  return {
    emoji: selectedMessage.emoji,
    title: selectedMessage.title,
    message: contextualMessage,
    bonuses,
    tip: getRandomFromArray(congratulationsData.tips),
    category: selectedMessage.category
  };
}

/**
 * Weighted random selection from array
 * @param {Array} items - Items with weight property
 * @returns {Object} Selected item
 */
function weightedRandomSelect(items) {
  if (!items || items.length === 0) return null;
  
  const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const item of items) {
    random -= item.weight;
    if (random <= 0) {
      return item;
    }
  }
  
  // Fallback to last item
  return items[items.length - 1];
}

/**
 * Get random item from array
 * @param {Array} array - Array to select from
 * @returns {*} Random item
 */
function getRandomFromArray(array) {
  if (!array || array.length === 0) return '';
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Get completion bonuses based on performance
 * @param {Object} performance - Performance metrics
 * @returns {Array} Array of bonus objects
 */
function getBonuses({ mistakes, completionTime, firstAttempt, streak }) {
  const bonuses = [];
  
  congratulationsData.completionBonuses.forEach(bonus => {
    switch (bonus.condition) {
      case 'all_moves_correct':
        if (mistakes === 0) bonuses.push(bonus);
        break;
      case 'fast_completion':
        if (completionTime > 0 && completionTime < 60) bonuses.push(bonus);
        break;
      case 'first_attempt':
        if (firstAttempt) bonuses.push(bonus);
        break;
      case 'streak':
        if (streak >= 3) bonuses.push(bonus);
        break;
      default:
        // No action needed for unknown bonus conditions
        break;
    }
  });
  
  return bonuses;
}

/**
 * Get all available congratulations categories
 * @returns {Array} Array of category names
 */
export function getAvailableCategories() {
  return [...new Set(congratulationsData.congratulations.map(msg => msg.category))];
}

/**
 * Get a specific congratulations message by category
 * @param {string} category - Category to select from
 * @returns {Object} Congratulations message object
 */
export function getCongratulationsByCategory(category) {
  const categoryMessages = congratulationsData.congratulations.filter(
    msg => msg.category === category
  );
  
  if (categoryMessages.length === 0) {
    return getCongratulationsMessage();
  }
  
  const selectedMessage = getRandomFromArray(categoryMessages);
  const message = getRandomFromArray(selectedMessage.messages);
  
  return {
    emoji: selectedMessage.emoji,
    title: selectedMessage.title,
    message,
    bonuses: [],
    tip: getRandomFromArray(congratulationsData.tips),
    category: selectedMessage.category
  };
}